import { Users, MessageSquare, Zap, TrendingUp, Activity, Inbox } from "lucide-react";
import type { DashboardOverview } from "@/types/api";
import { cn } from "@/lib/utils";

type ActivityItem = DashboardOverview["recentActivity"][number];

const ICON_CONFIG: Record<
  string,
  { Icon: typeof Users; badgeClass: string; iconColor: string }
> = {
  lead: {
    Icon: Users,
    badgeClass: "bg-blue-50 dark:bg-blue-950/40 border-blue-200/60 dark:border-blue-800/50",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  message: {
    Icon: MessageSquare,
    badgeClass: "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200/60 dark:border-emerald-800/50",
    iconColor: "text-emerald-600 dark:text-emerald-400",
  },
  integration: {
    Icon: Zap,
    badgeClass: "bg-purple-50 dark:bg-purple-950/40 border-purple-200/60 dark:border-purple-800/50",
    iconColor: "text-purple-600 dark:text-purple-400",
  },
  conversion: {
    Icon: TrendingUp,
    badgeClass: "bg-amber-50 dark:bg-amber-950/40 border-amber-200/60 dark:border-amber-800/50",
    iconColor: "text-amber-600 dark:text-amber-400",
  },
};

function formatTime(timestamp: string) {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

export function RecentActivityFeed({ activities }: { activities: ActivityItem[] }) {
  const safeActivities = Array.isArray(activities) ? activities : [];

  if (safeActivities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500">
          <Inbox className="h-5 w-5" />
        </div>
        <p className="mt-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200">No recent activity yet</p>
        <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
          Incoming messages and leads will appear here in real-time.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {safeActivities.map((activity) => {
        const config = ICON_CONFIG[activity.type] ?? {
          Icon: Activity,
          badgeClass: "bg-slate-50 dark:bg-slate-800 border-slate-200/60 dark:border-slate-700",
          iconColor: "text-slate-600 dark:text-slate-300",
        };
        const { Icon } = config;

        return (
          <div
            key={activity.id}
            className="flex items-start gap-3 rounded-xl border border-transparent p-2 transition-colors hover:border-slate-200/60 dark:hover:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/60"
          >
            <div
              className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border shadow-2xs",
                config.badgeClass,
                config.iconColor
              )}
            >
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium leading-snug text-slate-800 dark:text-slate-200">{activity.message}</p>
              <p className="mt-0.5 text-[10px] text-slate-400 dark:text-slate-500">
                {formatTime(activity.timestamp)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
